<div class="website-content" style="max-width:72rem;padding:0 var(--wa-spacing-x-large) var(--wa-spacing-x-large) var(--wa-spacing-x-large);">
	<div class="wa-stack wa-gap-xl">
		<div class="wa-stack wa-gap-m content-section" id="architecture">
			<wa-divider class="section-divider"></wa-divider>
			<div class="wa-stack wa-gap-s">
				<div class="hero-eyebrow" waCaption="s" waFontWeight="semibold" waText>ARCHITECTURE</div>
				<h2 waHeading="l" waText>How JWebMP Works</h2>
				<p waBody="m" waColorText="quiet" waText>A layered, modular architecture &mdash; each layer does one thing well.</p>
			</div>
			<div class="wa-stack wa-gap-m">
				<p waBody="m" waText>JWebMP is a layered architecture where each layer has a single responsibility. The foundation is GuicedEE &#40;dependency injection, SPI discovery, Vert.x lifecycle&#41;. JWebMP Core adds the HTML/CSS/JS component model. Plugins extend it with specific component libraries. The Angular layer generates the frontend. At runtime, Vert.x serves everything reactively.</p>
				<div class="mermaid-diagram">
					<markdown [data]="mdContent_2" mermaid></markdown>
				</div>
			</div>
		</div>
		<div class="wa-stack wa-gap-m content-section" id="stack">
			<wa-divider class="section-divider"></wa-divider>
			<div class="wa-stack wa-gap-s">
				<div class="hero-eyebrow" waCaption="s" waFontWeight="semibold" waText>STACK</div>
				<h2 waHeading="l" waText>Four-Layer Architecture</h2>
				<p waBody="m" waColorText="quiet" waText>Your app &rarr; Plugins &rarr; JWebMP Core &rarr; GuicedEE Foundation.</p>
			</div>
			<div class="wa-stack wa-gap-m">
				<p waBody="m" waText>The JWebMP stack is four layers deep. Your application sits at the top; everything below is framework infrastructure.</p>
				<div class="mermaid-diagram-wrapper">
					<div class="mermaid-diagram-label" waCaption="s" waFontWeight="semibold" waText>The JWebMP Stack</div>
					<div class="mermaid-diagram">
						<markdown [data]="mdContent_3" mermaid></markdown>
					</div>
				</div>
			</div>
		</div>
		<div class="wa-stack wa-gap-m content-section" id="build-pipeline">
			<wa-divider class="section-divider"></wa-divider>
			<div class="wa-stack wa-gap-s">
				<div class="hero-eyebrow" waCaption="s" waFontWeight="semibold" waText>BUILD</div>
				<h2 waHeading="l" waText>Build-Time Code Generation</h2>
				<p waBody="m" waColorText="quiet" waText>Java compiles &rarr; TypeScript generates &rarr; Angular builds &rarr; JAR packages.</p>
			</div>
			<div class="wa-stack wa-gap-m">
				<markdown [data]="mdContent_4"></markdown>
				<div class="mermaid-diagram-wrapper">
					<div class="mermaid-diagram-label" waCaption="s" waFontWeight="semibold" waText>Build Flow</div>
					<div class="mermaid-diagram">
						<markdown [data]="mdContent_5" mermaid></markdown>
					</div>
				</div>
			</div>
		</div>
		<div class="wa-stack wa-gap-m content-section" id="runtime">
			<wa-divider class="section-divider"></wa-divider>
			<div class="wa-stack wa-gap-s">
				<div class="hero-eyebrow" waCaption="s" waFontWeight="semibold" waText>RUNTIME</div>
				<h2 waHeading="l" waText>Reactive Server Architecture</h2>
				<p waBody="m" waColorText="quiet" waText>Vert.x event loop, Guice DI, call scopes, and SPA hosting.</p>
			</div>
			<div class="wa-stack wa-gap-m">
				<div class="wa-grid wa-gap-m" style="--min-column-size:20rem;">
					<wa-card appearance="outlined" class="feature-card">
						<div class="wa-stack wa-gap-s">
							<h3 class="feature-card-title" waHeading="m" waText>Boot &rarr; Inject &rarr; Serve</h3>
							<p waBody="m" waColorText="quiet" waText>Boot.main&#40;&#41; sets system properties and calls IGuiceContext.inject&#40;&#41;. Guice scans modules, discovers SPI implementations, binds services, and starts the Vert.x HTTP server &mdash; all from a single line.</p>
						</div>
					</wa-card>
					<wa-card appearance="outlined" class="feature-card">
						<div class="wa-stack wa-gap-s">
							<h3 class="feature-card-title" waHeading="m" waText>Vert.x Routes</h3>
							<p waBody="m" waColorText="quiet" waText>JWebMPVertx registers routes automatically: GET / for pages, POST /jwajax for AJAX events, GET /jwdata for data components, GET /jwcss for CSS, and /eventbus for STOMP WebSocket.</p>
						</div>
					</wa-card>
					<wa-card appearance="outlined" class="feature-card">
						<div class="wa-stack wa-gap-s">
							<h3 class="feature-card-title" waHeading="m" waText>Call Scopes</h3>
							<p waBody="m" waColorText="quiet" waText>Every HTTP request enters a Guice CallScope. RoutingContext, request, response, and session data are available via injection anywhere in the call chain.</p>
						</div>
					</wa-card>
					<wa-card appearance="outlined" class="feature-card">
						<div class="wa-stack wa-gap-s">
							<h3 class="feature-card-title" waHeading="m" waText>SPA Fallback</h3>
							<p waBody="m" waColorText="quiet" waText>Static assets are served from the Angular dist/ directory. Unmatched routes fall back to index.html for Angular Router client-side navigation.</p>
						</div>
					</wa-card>
				</div>
			</div>
		</div>
		<div class="wa-stack wa-gap-m content-section" id="data-flow">
			<wa-divider class="section-divider"></wa-divider>
			<div class="wa-stack wa-gap-s">
				<div class="hero-eyebrow" waCaption="s" waFontWeight="semibold" waText>DATA FLOW</div>
				<h2 waHeading="l" waText>Request, Event, and Message Paths</h2>
				<p waBody="m" waColorText="quiet" waText>HTTP for pages, AJAX for events, WebSocket for real-time.</p>
			</div>
			<div class="wa-stack wa-gap-m">
				<p waBody="m" waText>JWebMP supports three communication patterns: HTTP for page delivery, AJAX for event-driven DOM updates, and WebSocket &#40;STOMP&#41; for real-time push messaging via Vert.x Event Bus and RabbitMQ.</p>
				<div class="mermaid-diagram-wrapper">
					<div class="mermaid-diagram-label" waCaption="s" waFontWeight="semibold" waText>Request-Response Flow</div>
					<div class="mermaid-diagram">
						<markdown [data]="mdContent_6" mermaid></markdown>
					</div>
				</div>
			</div>
		</div>
		<div class="wa-stack wa-gap-m content-section" id="module-graph">
			<wa-divider class="section-divider"></wa-divider>
			<div class="wa-stack wa-gap-s">
				<div class="hero-eyebrow" waCaption="s" waFontWeight="semibold" waText>MODULES</div>
				<h2 waHeading="l" waText>JPMS Module Graph</h2>
				<p waBody="m" waColorText="quiet" waText>Explicit requires, explicit exports &mdash; no surprises.</p>
			</div>
			<div class="wa-stack wa-gap-m">
				<markdown [data]="mdContent_7"></markdown>
				<div class="mermaid-diagram-wrapper">
					<div class="mermaid-diagram-label" waCaption="s" waFontWeight="semibold" waText>Core Module Dependencies</div>
					<div class="mermaid-diagram">
						<markdown [data]="mdContent_8" mermaid></markdown>
					</div>
				</div>
				<div class="wa-cluster wa-gap-s">
					<wa-button [routerLink]="['/plugins']" variant="brand">Browse All Plugins</wa-button>
					<wa-button [routerLink]="['/capabilities']" appearance="outlined" variant="neutral">View Capabilities</wa-button>
				</div>
			</div>
		</div>
		<div class="wa-stack wa-gap-m content-section cta-section">
			<wa-divider class="section-divider"></wa-divider>
			<div class="wa-stack wa-gap-s">
				<h2 waHeading="l" waText>Ready to Build?</h2>
				<p waBody="m" waColorText="quiet" waText>From zero to production Angular app &mdash; entirely in Java.</p>
			</div>
			<div class="wa-stack wa-gap-m">
				<p waBody="l" waText>Stop context-switching between Java and TypeScript. Build your entire web application in the language you already know.</p>
				<div class="wa-cluster wa-gap-s hero-ctas">
					<wa-button [routerLink]="['/getting-started']" variant="brand">Get Started</wa-button>
					<wa-button [routerLink]="['/about']" appearance="outlined" variant="neutral">About JWebMP</wa-button>
					<wa-button [routerLink]="['/github']" appearance="outlined" variant="neutral">View on GitHub</wa-button>
				</div>
			</div>
		</div>
	</div>
</div>